"use client";

import { useRef, useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useIsMobile } from "@/hooks/use-mobile";
import { Card } from "@/components/ui/card";
import { SectionTitle } from "@/components/ui/section-title";
import { AutoFallbackImage } from "@/components/ui/auto-fallback-image";
import { CarouselDots } from "@/components/ui/carousel-dots";
import { MessageSquare } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import type { SmsDonation } from "@/types";

interface SmsDonationsSectionProps {
  smsDonations?: SmsDonation[];
  layout?: "grid" | "list" | "carousel";
  itemsPerView?: number;
  title?: string | null;
  subtitle?: string | null;
  showQuoteMarks?: boolean;
  quoteMarkSrc?: string | null;
}

function gridStyle(ipv: number) {
  return { gridTemplateColumns: `repeat(${ipv}, minmax(0, 1fr))` } as const;
}

function SmsCard({ donation }: { donation: SmsDonation }) {
  return (
    <a
      href={`sms:${donation.PhoneNumber};?&body=${encodeURIComponent(donation.MessageBody)}`}
      className="block"
    >
      <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
        <div className="relative bg-muted">
          <AutoFallbackImage
            imageUrl={donation.ImageUrl ?? null}
            imageId={donation.ImageId}
            alt={`${donation.PhoneNumber}`}
            className="object-cover w-full h-full"
          />
        </div>
      </Card>
    </a>
  );
}

function SmsListItem({ donation }: { donation: SmsDonation }) {
  return (
    <a
      href={`sms:${donation.PhoneNumber};?&body=${encodeURIComponent(donation.MessageBody)}`}
      className="block"
    >
      <Card className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer">
        <div className="flex items-center gap-4 p-4">
          <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-muted">
            <AutoFallbackImage
              imageUrl={donation.ImageUrl ?? null}
              imageId={donation.ImageId}
              alt={`${donation.PhoneNumber}`}
              className="object-cover w-full h-full"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-base text-foreground" dir="ltr">
              {donation.PhoneNumber}
            </p>
            {donation.MessageBody && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                {donation.MessageBody}
              </p>
            )}
          </div>
          <MessageSquare className="h-5 w-5 shrink-0 text-primary" />
        </div>
      </Card>
    </a>
  );
}

function SmsCarousel({
  smsDonations,
  itemsPerView,
}: {
  smsDonations: SmsDonation[];
  itemsPerView: number;
}) {
  const locale = useLocale();
  const isRTL = locale === "ar";
  const isMobile = useIsMobile();

  const autoplayRef = useRef(
    Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true }),
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: false,
      direction: isRTL ? "rtl" : "ltr",
      align: "start",
      containScroll: "trimSnaps",
    },
    isMobile ? [] : [autoplayRef.current],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnapCount, setScrollSnapCount] = useState(0);

  const onSelect = useCallback((api: NonNullable<typeof emblaApi>) => {
    setSelectedIndex(api.selectedScrollSnap());
    setScrollSnapCount(api.scrollSnapList().length);
  }, []);

  const scrollTo = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("reInit", onSelect);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  const gap = 10;
  const totalGap = (itemsPerView - 1) * gap;
  const slideBasis = `calc((100% - ${totalGap}px) / ${itemsPerView})`;

  return (
    <div className="relative">
      <div className="embla overflow-hidden" ref={emblaRef}>
        <div className="embla__container flex gap-2.5">
          {smsDonations.map((donation) => (
            <div
              key={donation.Id}
              className="embla__slide min-w-0"
              style={{ flex: `0 0 ${slideBasis}` }}
            >
              <SmsCard donation={donation} />
            </div>
          ))}
        </div>
      </div>
      {scrollSnapCount > 1 && (
        <div className="mt-6 flex justify-center">
          <CarouselDots
            count={scrollSnapCount}
            selectedIndex={selectedIndex}
            onSelect={scrollTo}
          />
        </div>
      )}
    </div>
  );
}

export function SmsDonationsSection({
  smsDonations = [],
  layout = "grid",
  itemsPerView = 4,
  title,
  subtitle,
  showQuoteMarks = false,
  quoteMarkSrc = null,
}: SmsDonationsSectionProps) {
  const t = useTranslations("home");
  if (!smsDonations || smsDonations.length === 0) {
    return null;
  }

  return (
    <section className="py-8 md:py-12">
      <div className="content-container">
        <div className="flex justify-center mb-6 md:mb-8">
          <SectionTitle
              align="center"
              subtitle={subtitle?.trim() || undefined}
              showQuoteMarks={showQuoteMarks}
              quoteMarkSrc={quoteMarkSrc}
            >
              {title?.trim() || t("smsDonationsTitle")}
            </SectionTitle>
        </div>

        {layout === "list" ? (
          <div className="flex flex-col gap-2 max-w-2xl mx-auto">
            {smsDonations.map((donation) => (
              <SmsListItem key={donation.Id} donation={donation} />
            ))}
          </div>
        ) : layout === "carousel" ? (
          <SmsCarousel
            smsDonations={smsDonations}
            itemsPerView={itemsPerView}
          />
        ) : (
          <div
            className="grid gap-2 md:gap-3 lg:gap-4"
            style={gridStyle(itemsPerView)}
          >
            {smsDonations.map((donation) => (
              <SmsCard key={donation.Id} donation={donation} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

